import { usePage } from "@inertiajs/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { JobRecommendation } from "@components/dashboard/job_recommendation_card.tsx";

type PageProps = {
  recommendationsPath: string;
};

const CONFIRMATION_WAIT_MS = 8000;

export const recommendationsQueryKey = (appliedJobPostId?: string) =>
  appliedJobPostId ? ["recommendations", appliedJobPostId] : ["recommendations"];

const buildURL = (basePath: string, appliedJobPostId?: string) =>
  appliedJobPostId
    ? `${basePath}?${new URLSearchParams({ applied_job_post_id: appliedJobPostId })}`
    : basePath;

const fetchRecommendations = async (
  basePath: string,
  appliedJobPostId?: string,
): Promise<JobRecommendation[]> => {
  const response = await fetch(buildURL(basePath, appliedJobPostId), {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
  return response.json();
};

const CONFIRMATION_OPTIONS = { staleTime: Infinity, gcTime: 5 * 60 * 1000, retry: false };
const DASHBOARD_OPTIONS = { retry: 1, staleTime: 0, refetchOnWindowFocus: false };

const queryOptions = (basePath: string, appliedJobPostId?: string) => ({
  queryKey: recommendationsQueryKey(appliedJobPostId),
  queryFn: () => fetchRecommendations(basePath, appliedJobPostId),
  ...(appliedJobPostId ? CONFIRMATION_OPTIONS : DASHBOARD_OPTIONS),
});

const useRecommendations = (appliedJobPostId?: string, { enabled = true } = {}) => {
  const { recommendationsPath } = usePage<PageProps>().props;

  return useQuery({ ...queryOptions(recommendationsPath, appliedJobPostId), enabled });
};

export const usePrepareConfirmationRecommendations = () => {
  const { recommendationsPath } = usePage<PageProps>().props;
  const queryClient = useQueryClient();

  return async (appliedJobPostId: string) => {
    const recommendations = queryClient
      .fetchQuery(queryOptions(recommendationsPath, appliedJobPostId))
      .catch(() => undefined);

    await Promise.race([
      recommendations,
      new Promise((resolve) => setTimeout(resolve, CONFIRMATION_WAIT_MS)),
    ]);

    queryClient.invalidateQueries({
      queryKey: recommendationsQueryKey(),
      exact: true,
      refetchType: "none",
    });
  };
};

export default useRecommendations;
